export const GENCOW_PLATFORM_WAKE_CHANNEL = "gencow_platform_wake_v1";
export const GENCOW_APP_WAKE_CHANNEL = "gencow_app_wake_v1";
export const LEGACY_REALTIME_OUTBOX_WAKE_CHANNEL = "gencow_realtime_outbox";

export const WAKE_SIGNAL_KINDS = [
  "realtime",
  "scheduler",
  "workflow",
  "rag_ingest",
  "email",
  "webhook",
] as const;

export type WakeSignalKind = (typeof WAKE_SIGNAL_KINDS)[number];
export type ParsedWakeSignalKind = WakeSignalKind | "unknown";

export type ParsedWakeSignal = {
  kind: ParsedWakeSignalKind;
  legacyAppName: string | null;
  malformed: boolean;
};

export const WAKE_SIGNAL_NOTIFY_SQL = "SELECT pg_notify($1, $2)" as const;

const WAKE_SIGNAL_KIND_SET = new Set<string>(WAKE_SIGNAL_KINDS);
const CHANNEL_PATTERN = /^[A-Za-z0-9_.:-]+$/;
const MAX_NOTIFY_PAYLOAD_BYTES = 7_900;
const encoder = new TextEncoder();

function byteLength(value: string): number {
  return encoder.encode(value).length;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function isWakeSignalKind(value: unknown): value is WakeSignalKind {
  return typeof value === "string" && WAKE_SIGNAL_KIND_SET.has(value);
}

export function normalizeWakeSignalChannel(channel: string | undefined, fallback: string): string {
  const value = channel ?? fallback;
  if (!CHANNEL_PATTERN.test(value) || byteLength(value) > 63) {
    throw new Error("PostgreSQL wake channel must be 1-63 bytes of [A-Za-z0-9_.:-]");
  }
  return value;
}

export function buildWakeSignalPayload(kind: WakeSignalKind): string {
  const payload = JSON.stringify({ kind });
  if (byteLength(payload) > MAX_NOTIFY_PAYLOAD_BYTES) {
    throw new Error("PostgreSQL wake payload exceeds NOTIFY payload limit");
  }
  return payload;
}

export function buildWakeNotifySql(params: {
  kind: WakeSignalKind;
  channel?: string;
  defaultChannel?: string;
}): { query: typeof WAKE_SIGNAL_NOTIFY_SQL; params: [string, string] } {
  return {
    query: WAKE_SIGNAL_NOTIFY_SQL,
    params: [
      normalizeWakeSignalChannel(params.channel, params.defaultChannel ?? GENCOW_APP_WAKE_CHANNEL),
      buildWakeSignalPayload(params.kind),
    ],
  };
}

export function parseWakeSignalPayload(payloadText: string): ParsedWakeSignal {
  let payload: unknown;
  try {
    payload = JSON.parse(payloadText || "{}");
  } catch {
    return { kind: "unknown", legacyAppName: null, malformed: true };
  }

  if (!isObject(payload)) {
    return { kind: "unknown", legacyAppName: null, malformed: false };
  }

  if ("kind" in payload) {
    return {
      kind: isWakeSignalKind(payload.kind) ? payload.kind : "unknown",
      legacyAppName: null,
      malformed: false,
    };
  }

  const legacyAppName = typeof payload.appName === "string" ? payload.appName : null;
  if (legacyAppName || typeof payload.eventCount === "number") {
    return { kind: "realtime", legacyAppName, malformed: false };
  }

  return { kind: "unknown", legacyAppName: null, malformed: false };
}
